import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getActivityLog, getActivityLogUsernames, ActivityLogEntry, ActivityLogFilters } from '@/api/activityLog';

/* ─────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────── */
function ErrMsg({ msg }: { msg: string }) {
  return msg ? <p className="text-red-600 text-sm font-georgia mt-1">{msg}</p> : null;
}

const inputCls = 'w-full border rounded px-2 py-1.5 text-sm font-georgia focus:outline-none focus:ring-1 focus:ring-teal mb-3 disabled:opacity-60 disabled:bg-gray-100';
const labelCls = 'block text-xs font-georgia text-gray-600 mb-0.5';

const ACTION_TYPES = [
  'LOGIN', 'LOGOUT',
  'FAMILY_CREATE', 'FAMILY_UPDATE', 'FAMILY_DELETE',
  'FAMILY_MEMBER_CREATE', 'FAMILY_MEMBER_UPDATE', 'FAMILY_MEMBER_DELETE',
  'ANBIYAM_CREATE', 'ANBIYAM_UPDATE', 'ANBIYAM_DELETE',
  'CERTIFICATE_CREATE', 'CERTIFICATE_DELETE',
  'CEMETERY_SAVE', 'CEMETERY_DELETE', 'CEMETERY_SUBSCRIPTION_SET', 'CEMETERY_SUBSCRIPTION_DELETE',
  'NON_PARISH_CEMETERY_CREATE', 'NON_PARISH_CEMETERY_DELETE', 'NON_PARISH_CEMETERY_SUBSCRIPTION_SET',
  'SUBSCRIPTION_SET', 'SUBSCRIPTION_DELETE_MONTH',
  'USER_SAVE', 'USER_DELETE', 'ROLE_SAVE', 'ROLE_DELETE',
  'SEARCH', 'FILTER', 'EXPORT', 'PRINT', 'VIEW',
];

const actionBadgeCls = (actionType: string) => {
  if (actionType.includes('DELETE')) return 'bg-red-50 text-red-700 border-red-200';
  if (actionType.includes('CREATE') || actionType === 'LOGIN') return 'bg-green-50 text-green-700 border-green-200';
  if (actionType.includes('UPDATE') || actionType.includes('SET') || actionType.includes('SAVE')) return 'bg-amber-50 text-amber-700 border-amber-200';
  if (actionType === 'EXPORT' || actionType === 'PRINT') return 'bg-blue-50 text-blue-700 border-blue-200';
  return 'bg-gray-50 text-gray-600 border-gray-200';
};

const fmtDate = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

/* ─────────────────────────────────────────────────────────────
   Page
───────────────────────────────────────────────────────────── */
export default function ActivityLogPage() {
  const { isAdmin } = useAuth();

  const blankFilters: ActivityLogFilters = { username: '', actionType: '', dateFrom: '', dateTo: '', page: 1, pageSize: 50 };
  const [filters, setFilters] = useState(blankFilters);
  const [applied, setApplied] = useState(blankFilters);

  const [entries,    setEntries]    = useState<ActivityLogEntry[]>([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState('');

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    setErr('');
    getActivityLog(applied)
      .then((res) => {
        setEntries(res.items);
        setTotal(res.total);
        setTotalPages(res.totalPages);
      })
      .catch(() => setErr('Failed to load activity log'))
      .finally(() => setLoading(false));
  }, [applied, isAdmin]);

  const [usernames, setUsernames] = useState<string[]>([]);
  useEffect(() => {
    if (!isAdmin) return;
    getActivityLogUsernames().then(setUsernames).catch(() => {/* dropdown just stays at All users */});
  }, [isAdmin]);

  const applyFilters = () => setApplied({ ...filters, page: 1 });
  const clearFilters  = () => { setFilters(blankFilters); setApplied(blankFilters); };
  const goToPage      = (p: number) => setApplied((a) => ({ ...a, page: p }));

  if (!isAdmin) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700 font-georgia">
          Access denied — Admin role required.
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-xl font-georgia font-bold text-navy">Activity Log</h1>
        <p className="text-sm text-gray-500 font-georgia">
          Audit trail of logins, edits, and other actions across the system
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className={labelCls}>Username</label>
            <select
              className={`${inputCls} mb-0`}
              value={filters.username}
              onChange={(e) => setFilters((f) => ({ ...f, username: e.target.value }))}
            >
              <option value="">All users</option>
              {usernames.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Action Type</label>
            <select
              className={`${inputCls} mb-0`}
              value={filters.actionType}
              onChange={(e) => setFilters((f) => ({ ...f, actionType: e.target.value }))}
            >
              <option value="">All actions</option>
              {ACTION_TYPES.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>From</label>
            <input
              type="date"
              className={`${inputCls} mb-0`}
              value={filters.dateFrom}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            />
          </div>
          <div>
            <label className={labelCls}>To</label>
            <input
              type="date"
              className={`${inputCls} mb-0`}
              value={filters.dateTo}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
            />
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={applyFilters}
              className="px-4 py-1.5 bg-teal text-white text-sm font-georgia rounded hover:bg-teal-700 whitespace-nowrap"
            >
              Apply
            </button>
            <button
              onClick={clearFilters}
              className="px-4 py-1.5 bg-white border text-gray-600 text-sm font-georgia rounded hover:bg-gray-50 whitespace-nowrap"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-x-auto">
        <div className="flex justify-between items-center gap-3 p-4 border-b">
          <p className="text-xs font-bold font-georgia text-gray-400 uppercase tracking-wide">
            {loading ? 'Loading…' : `${total} entr${total === 1 ? 'y' : 'ies'}`}
          </p>
        </div>

        <ErrMsg msg={err} />

        <div className="hidden sm:block">
        <table className="w-full text-sm font-georgia">
          <thead className="bg-navy text-white text-xs uppercase">
            <tr>
              {['When', 'User', 'Role', 'Action', 'Description', 'Entity'].map((h) => (
                <th key={h} className="px-3 py-2 text-left whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && entries.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No activity found.</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.activityId} className="border-b last:border-0 hover:bg-offwhite/60">
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{fmtDate(e.createdAt)}</td>
                <td className="px-3 py-2 whitespace-nowrap font-semibold text-navy">{e.username}</td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-600">{e.role ?? '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={`inline-block px-2 py-0.5 rounded border text-xs ${actionBadgeCls(e.actionType)}`}>
                    {e.actionType}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-700">{e.description ?? '—'}</td>
                <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-xs">
                  {e.entityType ? `${e.entityType}${e.entityId ? ' #' + e.entityId : ''}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Mobile: compact cards */}
        <div className="sm:hidden divide-y divide-gray-100">
          {!loading && entries.length === 0 && (
            <p className="px-3 py-6 text-center text-gray-400 text-sm">No activity found.</p>
          )}
          {entries.map((e) => (
            <div key={e.activityId} className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-navy font-georgia truncate">{e.username}</p>
                  <p className="text-xs text-gray-500 font-georgia">{fmtDate(e.createdAt)}</p>
                </div>
                <span className={`inline-block px-2 py-0.5 rounded border text-xs flex-shrink-0 ${actionBadgeCls(e.actionType)}`}>
                  {e.actionType}
                </span>
              </div>
              {e.description && <p className="text-xs text-gray-700 font-georgia mt-1.5">{e.description}</p>}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {e.role && <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5 font-georgia">{e.role}</span>}
                {e.entityType && (
                  <span className="text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 font-georgia">
                    {e.entityType}{e.entityId ? ` #${e.entityId}` : ''}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-between items-center gap-3 p-4 border-t">
            <p className="text-xs text-gray-500 font-georgia">
              Page {applied.page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                disabled={(applied.page ?? 1) <= 1}
                onClick={() => goToPage((applied.page ?? 1) - 1)}
                className="px-3 py-1 text-sm border rounded font-georgia disabled:opacity-40 hover:bg-gray-50"
              >
                ‹ Prev
              </button>
              <button
                disabled={(applied.page ?? 1) >= totalPages}
                onClick={() => goToPage((applied.page ?? 1) + 1)}
                className="px-3 py-1 text-sm border rounded font-georgia disabled:opacity-40 hover:bg-gray-50"
              >
                Next ›
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
